import AlarmService from "@/src/application/services/alarm-service";
import ApiService from "@/src/application/services/api-service";
import type Alarm from "@/src/domain/models/alarm";
import type PageLike from "@/src/domain/models/page-like";
import { BusinessException } from "@/src/domain/common/business-exception";
import { SeverityEnum } from "@/src/domain/enums/SeverityEnum";
import { StatusEnum } from "@/src/domain/enums/StatusEnum";
import { HttpStatusCode } from "axios";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { AlarmApi } from "@generated/n3-backend-api/n3-backend-open-api-gen/alarm-api";
import { Configuration } from "@generated/n3-backend-api/configuration";

@injectable()
export default class AlarmServiceImpl extends AlarmService {
  private readonly alarmApi: AlarmApi;

  constructor(@inject(ApiService) private readonly apiService: ApiService) {
    super();
    this.alarmApi = new AlarmApi(new Configuration(), "", this.apiService.getAxiosInstance());
  }
  async fetchAllPaginatedAlarms(
    deviceId: number,
    startDate: DateTime,
    endDate: DateTime
  ): Promise<Alarm[]> {
    const alarms: Alarm[] = [];
    for (let page = 1; ; page++) {
      // eslint-disable-next-line no-await-in-loop -- sequential pagination: each page depends on previous result
      // biome-ignore lint: sequential pagination — each page depends on whether the previous returned data
      const res = await this.fetchAlarmDataByDevice(deviceId, startDate, endDate, page);
      alarms.push(...res.data);
      if (res.data.length === 0) break;
    }
    return alarms;
  }

  async fetchAlarmDataByDevice(
    deviceId: number,
    startDate: DateTime | null,
    endDate: DateTime,
    currentPage: number
  ): Promise<PageLike<Alarm>> {
    const res = await this.alarmApi.measurements(
      deviceId,
      endDate.toUTC().toISO() ?? "",
      startDate?.toUTC().toISO() ?? undefined,
      100,
      currentPage
    );

    if (res.status != HttpStatusCode.Ok) throw new BusinessException("Failed to get alarms");

    const { data, ...rest } = res.data;
    return {
      ...rest,
      data: data.map<Alarm>((i) => ({
        count: i.count ?? 0,
        text: i.text ?? "",
        severity: (i.severity ?? SeverityEnum.WARNING) as SeverityEnum,
        source: i.source ?? "",
        type: i.type ?? "",
        time: DateTime.fromISO(i.time ?? ""),
        status: (i.status ?? StatusEnum.ACTIVE) as StatusEnum,
        id: i.id ?? "",
        isRead: i.isRead ?? false,
        display: i.display
          ? {
              title: i.display.title ?? "",
              text: i.display.text ?? "",
              detailText: i.display.detailText ?? "",
            }
          : undefined,
        address: i.address,
        serialNumber: i.serialNumber,
        deviceId: i.deviceId,
      })),
    };
  }

  async fetchActiveAlarmSummary(): Promise<Alarm[]> {
    const res = await this.alarmApi.summary(DateTime.now().toUTC().toISO() ?? "");

    if (res.status != HttpStatusCode.Ok)
      throw new BusinessException("Failed to get active alarm summary");

    return res.data.map<Alarm>((i) => ({
      count: i.count ?? 0,
      text: i.text ?? "",
      severity: (i.severity ?? SeverityEnum.WARNING) as SeverityEnum,
      source: i.source ?? "",
      type: i.type ?? "",
      time: DateTime.fromISO(i.time ?? ""),
      status: (i.status ?? StatusEnum.ACTIVE) as StatusEnum,
      id: i.id ?? "",
      isRead: i.isRead ?? false,
      display: i.display
        ? {
            title: i.display.title ?? "",
            text: i.display.text ?? "",
            detailText: i.display.detailText ?? "",
          }
        : undefined,
      address: i.address,
      serialNumber: i.serialNumber,
      deviceId: i.deviceId,
    }));
  }

  async fetchActiveAlarmCount(): Promise<number> {
    const res = await this.alarmApi.activeCount(DateTime.now().toUTC().toISO() ?? "");
    if (res.status != HttpStatusCode.Ok)
      throw new BusinessException("Failed to get active alarm count");
    return res.data.count ?? 0;
  }

  async markAlarmAsRead(id: string): Promise<void> {
    const res = await this.alarmApi.markAsRead(Number(id));
    if (res.status != HttpStatusCode.Ok)
      throw new BusinessException("Failed to mark alarm as read");
  }

  async markAllAsRead(): Promise<void> {
    const summary = await this.fetchActiveAlarmSummary();
    const unreadAlarms = summary.filter((a) => !a.isRead);
    await Promise.all(unreadAlarms.map((a) => this.markAlarmAsRead(a.id)));
  }
}
